import { useState, type ReactNode } from "react";
import { Button, Modal, TextInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { useMoney } from "@/shared/ui";
import { roundMoney } from "@/modules/pos/lib/pricing";
import type {
  SubstitutionIngredient,
  SubstitutionRemovable,
} from "@/lib/api/types";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * The waiter's open substitution, asked at the table (D61, D63).
 *
 * Two lists, deliberately asymmetric: what may come OFF is this dish's own
 * recipe, and what may go ON is the store's entire ingredient pool. That
 * asymmetry IS the feature — the guest asking for V7 is asking for something
 * nobody put on this menu (S1), while a swap that removes something the plate
 * does not contain would be charged for a change that never happened.
 *
 * The waiter never types a number. The dialog QUOTES the charge the server is
 * about to compute — `max(0, added − removed)` — and the server computes it
 * again for real at append. That is what keeps swapping out of D46's
 * hand-typed-price class, which is granted to no preset role.
 *
 * The composition rule is printed ON THIS DIALOG, not left in a code comment.
 * D54.14 exists because "the tier applies on top of an override" was true only
 * in a comment: a manager typed 200 during a happy hour and the guest paid 160.
 */
export function SubstitutionPicker({
  item,
  removable,
  pool,
  loadFailed,
  initialSwaps,
  allowEmpty = false,
  title,
  confirmLabel,
  notice,
  onCancel,
  onConfirm,
}: {
  /**
   * Only the name is read, and the type says so: the ADD path passes a
   * `MenuItem` off the menu, and the AMEND path (B19) passes a plate that is
   * already on the bill and has no menu item behind it — it has a snapshot,
   * which is the whole point of a snapshot.
   */
  item: { nameAr: string };
  removable: SubstitutionRemovable[];
  pool: SubstitutionIngredient[];
  /** The read failed — say so rather than showing an empty picker. */
  loadFailed: boolean;
  /**
   * The swaps the plate ALREADY carries, when this dialog is amending one
   * (B19) rather than composing a new plate.
   *
   * Seeded into `chosen`, so the waiter sees what is on the plate and can take
   * a swap off by tapping it — the same gesture that removes one they just
   * added. Undefined on the add path, which is every other caller.
   */
  initialSwaps?: [SubstitutionRemovable, SubstitutionIngredient][];
  /**
   * Let Confirm through with NOTHING chosen.
   *
   * False on the add path, where an empty dialog is a waiter who tapped the
   * wrong button and Cancel is the honest answer. True when amending, where an
   * empty set is a real instruction: "take the swaps back off this plate".
   */
  allowEmpty?: boolean;
  /** Overridden when amending — "change" rather than "swap an ingredient". */
  title?: string;
  confirmLabel?: string;
  /**
   * A sentence above the actions, for something the waiter must know BEFORE
   * they confirm — the paper-kitchen warning, and how many plates the change
   * lands on. Rendered where the eye already is on its way to the button
   * rather than at the top of a dialog that scrolls.
   */
  notice?: ReactNode;
  onCancel: () => void;
  onConfirm: (
    substitutions: {
      removesIngredientId: string;
      addsIngredientId: string;
    }[],
    /**
     * WHAT THIS DIALOG JUST QUOTED, so the caller can keep saying it (D108).
     *
     * On the ADD path the option picker opens next and states the line total;
     * before this argument existed it quoted base + addons and silently dropped
     * the surcharge, so the LAST number a waiter saw before committing was
     * short by the swap. A flat surcharge made that a few pounds; a rate on a
     * 300 g line makes it tens.
     *
     * Passed rather than recomputed because this component already holds the
     * only correct arithmetic for it — recomputing in the caller would be the
     * second implementation of the charge rule, which is what the server mirror
     * exists to avoid.
     */
    quotedCharge: number,
  ) => void;
}) {
  const t = useTranslate();
  const money = useMoney();
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [addId, setAddId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  /**
   * Swaps already settled on this plate, in the order they were made.
   *
   * "No chicken, no onions, double beef" is one order, and the server has
   * accepted a LIST since D61 — up to `MAX_LINE_SUBSTITUTIONS`. A dialog that
   * could only ever send one sent the second request to the kitchen note
   * instead, where it moves no money and depletes no stock, which is the hole
   * swaps exist to close.
   */
  const [chosen, setChosen] = useState<
    [SubstitutionRemovable, SubstitutionIngredient][]
  >(initialSwaps ?? []);

  /** The server's own cap, mirrored so the dialog cannot build a refusal. */
  const MAX_SWAPS = 5;

  /** Already taken off by a swap settled earlier in this dialog. */
  const removedAlready = (ingredientId: string) =>
    chosen.some(([off]) => off.ingredientId === ingredientId);
  /** Already put ON by one. */
  const addedAlready = (ingredientId: string) =>
    chosen.some(([, on]) => on.ingredientId === ingredientId);

  /*
   * The chips the waiter may still take off: not one already taken off (the
   * server refuses DUPLICATE_REMOVAL), and not one another swap just put ON
   * (CHAINED_SWAP). Filtering here rather than letting the pick be refused is
   * the same rule the unit filter follows.
   */
  const removableNow = removable.filter(
    (row) =>
      !removedAlready(row.ingredientId) && !addedAlready(row.ingredientId),
  );

  const removed =
    removableNow.find((row) => row.ingredientId === removeId) ?? null;

  /**
   * What may replace the chosen ingredient.
   *
   * Filtered by UNIT, because there is no conversion engine (B11) and the
   * server refuses a cross-unit swap outright. Filtering here rather than
   * letting the waiter pick and be refused is the difference between a list
   * that cannot lie and an error after they have told the guest yes. The
   * removed ingredient is dropped too — swapping a thing for itself is refused
   * before the command is even claimed.
   */
  const candidates = !removed
    ? []
    : pool.filter(
        (row) =>
          row.ingredientId !== removed.ingredientId &&
          row.unit === removed.unit &&
          // Adding back what another swap just took off bills both changes and
          // cooks neither — the server's CHAINED_SWAP refusal, kept off the
          // screen.
          !removedAlready(row.ingredientId),
      );

  /**
   * The pool is the whole store, and a real kitchen has more ingredients than
   * fit on a phone. Thumbing through sixty chips at a table mid-service is how
   * a feature gets abandoned for a free-text note.
   */
  const query = search.trim().toLocaleLowerCase();
  const shown = query
    ? candidates.filter((row) => row.name.toLocaleLowerCase().includes(query))
    : candidates;

  const added = candidates.find((row) => row.ingredientId === addId) ?? null;

  /** Either side unpriced = free, and flagged into the shift review (S5). */
  const unpricedPair = (
    off: SubstitutionRemovable,
    on: SubstitutionIngredient,
  ) => off.sellPrice === null || on.sellPrice === null;
  const unpriced =
    (removed !== null && added !== null && unpricedPair(removed, added)) ||
    chosen.some(([off, on]) => unpricedPair(off, on));

  /**
   * The quote, mirroring `substitutionCharge` on the server exactly. Two rules,
   * in this order: an unpriced side makes the whole swap free — NOT a null read
   * as zero, which would quote the guest a price the bill will never take —
   * and a priced difference is clamped at zero, because a swap can only ADD
   * (S4), so a downgrade shows no difference and refunds nothing.
   *
   * SINCE D108 EITHER SIDE MAY BE A RATE, so each is brought to a plate amount
   * before the subtraction, over the REMOVED line's recipe quantity — the same
   * number the server prices with, and the reason it is on the wire.
   *
   * A MISSING `recipeQuantity` RESOLVES A RATE TO ZERO, AND THE SERVER DOES NOT
   * DO THAT — an earlier version of this comment claimed it did, which was
   * wrong and worth correcting rather than deleting. `resolveSubstitutions`
   * reads the quantity off a recipe line whose membership it has already
   * asserted, so the server always prices over the real number. The asymmetry
   * is deliberate but it is a FALLBACK, not a mirror: it exists so a payload
   * from before D108 — where every ingredient is `FLAT` and the quantity is
   * never read — renders instead of throwing.
   *
   * On a `PER_UNIT` pair that fallback is not a quote at all, and `unpriceable`
   * below is what says so. It can only be reached when the removed ingredient
   * is missing from `removable` — the dish's recipe changed under a plate
   * already rung up — and the server's answer to that swap is a refusal
   * (`NOT_ON_RECIPE`), not a price. So the dialog no longer quotes free on it:
   * it names the pair, closes the button, and leaves the one gesture that
   * fixes it — tapping the swap off — working.
   *
   * Rounded ONCE at the end, through the shared `roundMoney`, because that is
   * what the server does — two roundings would meet as a piastre that came from
   * nowhere, on the one number the waiter says out loud.
   */
  const plateAmount = (
    side: SubstitutionIngredient,
    pricingQuantity: number,
  ): number | null =>
    side.sellPrice === null
      ? null
      : side.sellPriceBasis === "PER_UNIT"
        ? side.sellPrice * pricingQuantity
        : side.sellPrice;

  /**
   * A pair whose charge cannot be WORKED OUT, as against one that is free.
   *
   * Free is a decision (D64: an unpriced side goes out at no charge and is
   * flagged). This is the absence of an input: a rate needs the quantity the
   * plate carries of the ingredient coming off, and there is none when that
   * ingredient is no longer on the dish's recipe. Quoting zero there told a
   * waiter "no difference" about a swap the server will refuse outright.
   *
   * Only ever true on the AMEND path, and only after a recipe edit under a
   * plate that is already on a bill. A new plate's removables come from the
   * recipe by construction.
   */
  const unpriceable = (
    off: SubstitutionRemovable,
    on: SubstitutionIngredient,
  ): boolean =>
    off.recipeQuantity === undefined &&
    (off.sellPriceBasis === "PER_UNIT" || on.sellPriceBasis === "PER_UNIT");

  const chargeOf = (
    off: SubstitutionRemovable,
    on: SubstitutionIngredient,
  ): number => {
    const pricingQuantity = off.recipeQuantity ?? 0;
    const offAmount = plateAmount(off, pricingQuantity);
    const onAmount = plateAmount(on, pricingQuantity);
    return offAmount === null || onAmount === null
      ? 0
      : roundMoney(Math.max(0, onAmount - offAmount));
  };

  const charge = roundMoney(
    chosen.reduce((sum, [off, on]) => sum + chargeOf(off, on), 0) +
      (removed && added ? chargeOf(removed, added) : 0),
  );

  /** Every swap the confirm button would send: the settled ones plus this one. */
  /**
   * The unit, short. Falls back to whatever the server sent for anything
   * outside the three canonical units, rather than printing a blank.
   */
  const unitLabel = (unit: string): string => {
    const key = unit.toLowerCase();
    return key === "g" || key === "ml" || key === "piece"
      ? t((m) => m.units[key])
      : unit;
  };

  /**
   * What one candidate chip says about price.
   *
   * `null` for an unpriced ingredient — the dialog already says the whole swap
   * is free, in words, and a "0.00" here would argue with it.
   */
  const priceChipLabel = (row: SubstitutionIngredient): string | null => {
    if (row.sellPrice === null) return null;
    if (row.sellPriceBasis !== "PER_UNIT") return money(row.sellPrice);
    const quantity = removed?.recipeQuantity ?? 0;
    /*
     * WRAPPED IN AN LTR ISOLATE, and a browser round on 2026-09-17 is why.
     *
     * The chip is `<arabic name><this suffix>`, and the suffix opens with a
     * number. Under bidi reordering that leading digit is pulled against the
     * Arabic, so `لحم مفروم` + `40 EGP · 0.25/g × 160` rendered as
     * `40لحم مفروم EGP · 0.25/g × 160` — the price reading as part of the name
     * and the working scattered around it. U+2066/U+2069 isolate the whole
     * suffix so it lays out left-to-right as one unit whatever sits beside it.
     *
     * It matters more here than on a FLAT chip, which carries one short number:
     * this is the arithmetic a waiter reads out to defend the charge, and a
     * jumbled version of it is worse than none.
     */
    return `\u2066${money(row.sellPrice * quantity)} · ${row.sellPrice}/${unitLabel(row.unit)} × ${quantity}\u2069`;
  };

  const pending: [SubstitutionRemovable, SubstitutionIngredient][] =
    removed && added ? [...chosen, [removed, added]] : chosen;

  /*
   * Over `pending`, not `chosen`: the pair the waiter is composing right now
   * counts too, and on the amend path a seeded swap is already in `chosen`.
   * One such pair closes the button for the whole dialog, because the request
   * carries the plate's swaps as ONE list — the server takes all of them or
   * none, so there is no "save the others" to offer except by taking this one
   * off, which is a tap on the chip.
   */
  const unpriceablePending = pending.some(([off, on]) => unpriceable(off, on));

  const nothingToRemove = !loadFailed && removable.length === 0;

  /*
   * A swap half-built or newly banked. `chosen` opens as the plate's existing
   * swaps, so a LENGTH change is an edit either way — banked one more, or (via
   * the row's own remove) taken one off.
   */
  const guard = useDiscardGuard(
    removeId !== null ||
      addId !== null ||
      chosen.length !== (initialSwaps?.length ?? 0),
    onCancel,
  );

  const chip = (
    label: string,
    isOn: boolean,
    onClick: () => void,
    testId: string,
    suffix?: string,
    /**
     * B18 — "the kitchen says this is finished", as its own pill rather than
     * one more item in `suffix`.
     *
     * The two claims on this row are deliberately unlike each other, and that
     * IS the founder's answer: `suffix` carries the price and the cached
     * `outOfStock` reading — arithmetic over a balance nobody may have counted
     * — while this is a person who looked. An ingredient nobody tracks sits at
     * zero and reads "out" every night, so a single muted string for both
     * would train the floor to skip past the one worth reading.
     *
     * Still advisory: neither this nor `suffix` disables the chip, because the
     * swap goes through either way.
     */
    warn?: string,
  ) => (
    <button
      key={testId}
      type="button"
      aria-pressed={isOn}
      onClick={onClick}
      data-testid={testId}
      className={[
        // min-h-11: tapped with a thumb, mid-service, at a table.
        "min-h-11 rounded-full border px-4 py-2.5 text-sm font-bold transition-colors",
        isOn
          ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
          : warn
            ? // Bordered in the warning tone when unselected, so the flag is
              // visible while scanning a wall of chips rather than only after
              // reading each label. Tone AND the word — never colour alone.
              "border-[color:var(--warning)] text-[color:var(--text-secondary)] hover:text-[color:var(--text)]"
            : "border-[color:var(--border-strong)] text-[color:var(--text-secondary)] hover:text-[color:var(--text)]",
      ].join(" ")}
    >
      {/* One word in the chip and the number beside it: `Chip` is
          `whitespace-nowrap` with no `overflow-hidden`, and Arabic must carry
          no letter-spacing — it prises the joined script apart. */}
      {label}
      {suffix ? (
        <span className="ms-1.5 tabular-nums opacity-80">{suffix}</span>
      ) : null}
      {warn ? (
        <span
          className={[
            "ms-1.5 rounded-full px-1.5 py-0.5 text-[length:var(--font-size-2xs)] font-extrabold",
            // On the selected chip the accent already owns the background, so
            // the pill borrows contrast from it rather than painting a second
            // solid colour on top of a solid colour.
            isOn
              ? "bg-white/25 text-[color:var(--accent-contrast)]"
              : "bg-[color-mix(in_oklab,var(--warning)_16%,var(--surface))] text-[color:var(--warning)]",
          ].join(" ")}
        >
          {warn}
        </span>
      ) : null}
    </button>
  );

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="md"
      /*
       * =====================================================================
       * THIS DIALOG KEEPS ITS ACTIONS IN THE BODY. DO NOT "FINISH THE JOB".
       * =====================================================================
       *
       * Slice 4 moved 29 hand-built action rows into `Modal`'s footer, because
       * that footer sits OUTSIDE `overflow-y-auto` and a primary action can
       * therefore never fall below its own dialog's fold. This one is the
       * documented exception (decision UX-round D10, veto V4), and `hideFooter` here is
       * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
       * a decision rather than residue.
       *
       * WHY. `swap-notice` carries `pos.swapAmendKitchenMayHaveStarted` /
       * `swapAmendTellKitchen` — the one place in this product that tells a
       * waiter to go and speak to a human before touching a plate the kitchen
       * may already have started. It is placed LAST in the body so it is read
       * on the way to the button.
       *
       * Move the buttons into `footerActions` and the existing ordering
       * assertion STAYS GREEN — the footer renders after the body, so
       * `compareDocumentPosition` still reports FOLLOWING — while the thing it
       * protects is destroyed: the confirm becomes reachable without ever
       * scrolling past the notice, because the footer is outside the scroller
       * and the ingredient lists above are long enough to push a warning off
       * screen. A false green, on the one warning that tells a waiter to talk
       * to the kitchen.
       *
       * `SubstitutionPicker.spec.tsx` now asserts the mechanism and not just
       * the order: the confirm must be a DESCENDANT of the scrolling body.
       * That assertion is what makes this comment enforceable.
       *
       * The other safe alternative V4 allows — footer actions PLUS the notice
       * mirrored into `footerLeft` — was not taken: `footerLeft` is a 12px
       * muted status slot in a non-wrapping row, which is the smaller type and
       * the squeezed buttons the browser round already found here once.
       */
      hideFooter
      title={title ?? t((m) => m.pos.swapTitle)}
      subtitle={item.nameAr}
      closeLabel={t((m) => m.common.cancel)}
    >
      <div className="flex flex-col gap-6 py-1">
        {loadFailed ? (
          <p className="text-sm font-semibold text-[color:var(--danger)]">
            {t((m) => m.pos.swapFailed)}
          </p>
        ) : null}
        {nothingToRemove ? (
          <p className="text-sm text-[color:var(--text-secondary)]">
            {t((m) => m.pos.swapNothingToRemove)}
          </p>
        ) : null}

        {chosen.length > 0 ? (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-3 text-sm font-bold text-[color:var(--text-secondary)]">
              {t((m) => m.pos.swapChosen)}
            </legend>
            <div className="flex flex-wrap gap-2.5">
              {chosen.map(([off, on], index) => (
                <button
                  key={`${off.ingredientId}-${on.ingredientId}`}
                  type="button"
                  onClick={() =>
                    setChosen((current) =>
                      current.filter((_, at) => at !== index),
                    )
                  }
                  data-testid={`swap-chosen-${off.ingredientId}`}
                  // Tapping it takes it back off: a waiter who mis-picks at a
                  // table must not have to cancel the whole dialog and start
                  // the order again.
                  className="min-h-11 rounded-full border border-[color:var(--accent)] bg-[color:var(--accent)] px-4 py-2.5 text-sm font-bold text-[color:var(--accent-contrast)]"
                >
                  {`− ${off.name} ⟵ + ${on.name}`}
                  <span className="ms-1.5 opacity-80">×</span>
                </button>
              ))}
            </div>
          </fieldset>
        ) : null}

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-3 text-sm font-bold text-[color:var(--text-secondary)]">
            {t((m) => m.pos.swapRemoveLabel)}
          </legend>
          <div className="flex flex-wrap gap-2.5">
            {removableNow.map((row) =>
              chip(
                row.name,
                row.ingredientId === removeId,
                () => {
                  setRemoveId(row.ingredientId);
                  // The candidate list is scoped to the removed ingredient's
                  // unit, so a stale pick could otherwise survive a change of
                  // mind and send a cross-unit swap the server would refuse.
                  setAddId(null);
                },
                `swap-remove-${row.ingredientId}`,
                undefined,
                // Flagged on the REMOVE side too (B18): a finished ingredient
                // on the dish's own recipe is usually the reason the waiter
                // opened this dialog, and it is the one they should be
                // reaching for.
                row.markedOut ? t((m) => m.pos.swapMarkedOut) : undefined,
              ),
            )}
          </div>
        </fieldset>

        {removed ? (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-3 text-sm font-bold text-[color:var(--text-secondary)]">
              {t((m) => m.pos.swapAddLabel)}
            </legend>
            {/* Search, once the wall of chips is longer than a thumb wants to
                read. Below that it is one more control in the way. */}
            {candidates.length > 8 ? (
              <TextInput
                name="swapSearch"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t((m) => m.pos.swapSearch)}
                data-testid="swap-search"
              />
            ) : null}
            <div className="flex flex-wrap gap-2.5">
              {shown.map((row) =>
                chip(
                  row.name,
                  row.ingredientId === addId,
                  () => setAddId(row.ingredientId),
                  `swap-add-${row.ingredientId}`,
                  [
                    /*
                     * WHAT THIS INGREDIENT IS WORTH ON THIS PLATE, never the
                     * bare catalogue number (D108).
                     *
                     * A `PER_UNIT` row's stored price is per gram, and printing
                     * "0.30" beside a chip on a dialog whose confirm button
                     * says "+45" is the defect D64 was written to stop — two
                     * numbers on one screen that cannot both be said out loud.
                     * So the chip shows the plate amount, and the rate rides
                     * behind it as the working: 45.00 · 0.30/g × 150.
                     */
                    priceChipLabel(row),
                    // The CACHED reading (D68). Advisory, never a block: the
                    // swap goes through, and this is the last moment the
                    // waiter can know before promising it at the table. It
                    // stays a muted string beside the price, because that is
                    // the weight it deserves — an ingredient nobody counts
                    // sits at zero and says this every night.
                    row.outOfStock ? t((m) => m.pos.swapOutOfStock) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined,
                  // A PERSON's call (B18), and the whole reason it is not in
                  // the list above: the two make different claims, and only
                  // this one means somebody looked.
                  row.markedOut ? t((m) => m.pos.swapMarkedOut) : undefined,
                ),
              )}
            </div>
            {shown.length === 0 ? (
              <p
                className="text-sm text-[color:var(--text-secondary)]"
                data-testid="swap-no-match"
              >
                {t((m) => m.pos.swapNoMatch)}
              </p>
            ) : null}
            {/* One more swap on the same plate. The pair is banked and the two
                lists reset, so "no chicken, no onions" is one dialog and one
                append rather than a note. */}
            {removed && added && chosen.length + 1 < MAX_SWAPS ? (
              <button
                type="button"
                onClick={() => {
                  setChosen((current) => [...current, [removed, added]]);
                  setRemoveId(null);
                  setAddId(null);
                  setSearch("");
                }}
                data-testid="swap-add-another"
                className="min-h-11 self-start rounded-full border border-[color:var(--border-strong)] px-4 py-2.5 text-sm font-bold text-[color:var(--text-secondary)] hover:text-[color:var(--text)]"
              >
                {t((m) => m.pos.swapAddAnother)}
              </button>
            ) : null}
          </fieldset>
        ) : null}

        {unpriceablePending ? (
          /* Above the free-swap note and in the danger tone, because the two
             say opposite things and only this one stops the dialog. */
          <p
            className="text-sm font-semibold text-[color:var(--danger)]"
            data-testid="swap-unpriceable-note"
          >
            {t((m) => m.pos.swapUnpriceableNote)}
          </p>
        ) : null}

        {unpriced ? (
          <p
            className="text-sm text-[color:var(--text-secondary)]"
            data-testid="swap-unpriced-note"
          >
            {t((m) => m.pos.swapUnpricedNote)}
          </p>
        ) : null}

        {/*
          The last thing before the buttons, so it is read on the way to them.
          NOT inside `footerActions`: that container is a non-wrapping flex row
          (`*:flex-1` when inset), so a full-width sentence in there does not
          push the buttons onto their own line — it sits BESIDE them and squeezes
          both. Seen in the browser round.
        */}
        {notice ? (
          <p
            className="text-sm font-semibold text-[color:var(--warning-700,var(--text-secondary))]"
            data-testid="swap-notice"
          >
            {notice}
          </p>
        ) : null}

        {/* THE ACTIONS, IN THE BODY. See the block comment on `<Modal>` above
            before moving these: they are inside `overflow-y-auto` on purpose,
            so the confirm cannot be reached without scrolling past the notice
            immediately above it. */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="touch"
            className="flex-1"
            onClick={guard.requestClose}
            data-testid="swap-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="touch"
            className="flex-1"
            disabled={
              (pending.length === 0 && !allowEmpty) || unpriceablePending
            }
            onClick={() =>
              !unpriceablePending &&
              (pending.length > 0 || allowEmpty) &&
              onConfirm(
                pending.map(([off, on]) => ({
                  removesIngredientId: off.ingredientId,
                  addsIngredientId: on.ingredientId,
                })),
                charge,
              )
            }
            data-testid="swap-confirm"
          >
            {/* When amending, an empty dialog is "remove every swap" — the
                button has to SAY that, or a waiter reads a bare "Save" and
                cannot tell it apart from doing nothing. */}
            {pending.length === 0 && allowEmpty
              ? t((m) => m.pos.swapAmendClear)
              : (confirmLabel ?? t((m) => m.pos.swapConfirm))}
            {/* No figure at all while a pair cannot be priced: "No difference"
                is the one thing this button must not say about a swap whose
                difference is unknown. */}
            {pending.length > 0 && !unpriceablePending
              ? ` · ${charge > 0 ? `+${money(charge)}` : t((m) => m.pos.swapFree)}`
              : ""}
          </Button>
        </div>

        {/*
          D54.14. The rule that decides what the guest actually pays lives HERE,
          on the screen where the decision is taken — not in a comment, and not
          in a doc nobody opens at a table.
        */}
        <div className="flex flex-col gap-2 rounded-xl border border-[color:var(--border)] p-4">
          <p className="text-sm text-[color:var(--text-secondary)]">
            {t((m) => m.pos.swapCompositionRule)}
          </p>
          <p className="text-sm text-[color:var(--text-secondary)]">
            {t((m) => m.pos.swapNeverRefunds)}
          </p>
        </div>
      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="swap-discard-prompt"
    />
    </>
  );
}
